// Domain types shared by the headless core, the web presenter, the laptop
// apps, the API and the CLI. Nothing here imports three.js or the DOM.

/** The devices on the shelf. Each id names one emulated machine. */
export type DeviceId = "psp" | "vita" | "3ds" | "ipod-touch" | "android";
export const DEVICE_IDS: readonly DeviceId[] = ["psp", "vita", "3ds", "ipod-touch", "android"];

/** What a game's installation package can be built for: each device of the shelf. */
export type NativeTarget = DeviceId;
export const NATIVE_TARGETS: readonly NativeTarget[] = DEVICE_IDS;

export type AgentId = "claude" | "codex";

/** What a game is called until its maker names it. The command line sends no second build of a game still called this. */
export const DEFAULT_TITLE = "Untitled Game";

/** A colourway for a device. `base` skins ship with every account. */
export interface SkinInfo {
  id: string;
  name: string;
  /** Swatch colour shown in menus, `#rrggbb`. */
  swatch: string;
  /** `base` colours ship with every account; `member` colours come with the membership. */
  tier: "base" | "member";
  owned: boolean;
}

export interface DeviceInfo {
  id: DeviceId;
  name: string;
  /** Logical viewport of the primary screen. */
  viewport: [number, number];
  /** Second screen (the 3DS touch screen). */
  auxiliary?: [number, number];
  input: { buttons: boolean; touch: boolean };
  /** The skin currently worn: one of `skins`, or CUSTOM_SKIN for the account's own colour. */
  skin: string;
  skins: SkinInfo[];
  /**
   * The colour the account mixed for this device, `#rrggbb`; null until one
   * is chosen. Choosing one is a membership feature. It is worn when `skin`
   * is CUSTOM_SKIN.
   */
  color: string | null;
}

/** Where an app came from. */
export type AppOrigin = "starter" | "mine" | "shared";

/**
 * How a game is built. A `pocket` game is a Pocket app: a device session
 * compiles it and the room runs its bundle on the emulated devices. A `site`
 * game is built outside Pocket Studio: its author uploads a static web build
 * and prepared packages, and it has no bundle a device in the room can run.
 */
export type AppKind = "pocket" | "site";

/** The installation package an author uploaded for one target. */
export interface PackageInfo {
  target: NativeTarget;
  /** The name the file downloads under, as the author named it: `EBOOT.PBP`, `pocket-tokyo.vpk`, `PocketTokyo-psp.zip`. */
  filename: string;
  /** Size in bytes. */
  size: number;
  /** The version the author gave it, such as `0.1.0`; empty when none. */
  version: string;
  uploadedAt: number;
  /**
   * Pocket Studio makes this package from the game's build for the device
   * when someone first downloads it (docs/publishing.md). Its size is 0
   * until then.
   */
  made?: true;
}

/** The static web build that is live at a `site` game's address. */
export interface SiteInfo {
  /**
   * The devices the site plays as, as its entry page names them
   * (`<meta name="pocket-devices">`, `src/core/platforms.ts`). Absent for a
   * site deployed before the page had to say.
   */
  devices?: DeviceId[];
  files: number;
  /** Total size in bytes. */
  bytes: number;
  updatedAt: number;
}

/** What drew a picture of a listing: the browser version, or a device. */
export type ListingFrom = "browser" | DeviceId;

/** One clip or still of a game's listing. Every address is absolute, on the Studio's host. */
export interface ListingMedia {
  kind: "video" | "image";
  /** The clip (MP4, H.264, no sound) or the still. */
  url: string;
  /** A clip's poster: a JPEG of the clip's pixel size. Null for a still. */
  poster: string | null;
  width: number;
  height: number;
  /** A clip's length in whole seconds; null for a still. */
  seconds: number | null;
  from: ListingFrom;
  caption: string;
}

/**
 * A game's listing: what its page on the Studio shows beside its title and
 * its tagline. Its author uploads it with `pocket-studio listing`
 * (docs/publishing.md). The first entry of `media` is the lead.
 */
/**
 * A listing's words in a language other than English. Each part is absent
 * where the author wrote none: a page shows the English one there.
 * `captions[i]` is the caption of `media[i]`, or null.
 */
export interface ListingTranslation {
  tagline?: string;
  description?: string[];
  captions?: Array<string | null>;
}

export interface ListingInfo {
  description: string[];
  media: ListingMedia[];
  /** The words in other languages, by language (`translations.ja.tagline`). Absent when there are none. */
  translations?: Partial<Record<"ja", ListingTranslation>>;
  /** The share picture, 1200 by 630: what a link preview shows. */
  card: string;
  updatedAt: number;
}

/** An editor's takedown of one game: the sentence its author reads, and the moment it was set. */
export interface Takedown {
  reason: string;
  at: number;
}

export interface AppInfo {
  kind: AppKind;
  /**
   * The label of the game's own host, such as `tokyo`. A published game of a
   * signed-in account has one; a draft keeps the one it had. Null for a
   * guest's game and for a starter.
   */
  slug: string | null;
  /**
   * The game's public address, such as `https://tokyo.studio.pocket.nexus`:
   * its web version, for anyone. Null unless the game is published and has a
   * slug.
   */
  url: string | null;
  /** Whether its author allows downloads of its installation packages. */
  allowNative?: boolean;
  /**
   * The earlier versions kept of each device's package, newest first within
   * a device: for the game's author alone (GET /api/cli/apps/:id). Everyone
   * else reads them from GET /api/games/:slug.
   */
  past?: Array<Omit<PackageInfo, "made">>;
  /**
   * Set while Pocket Nexus has taken the game down: the reason an editor
   * gave, and when. Only its author's view of the game carries it. A game
   * taken down is a draft that cannot be published until an editor lifts it.
   */
  takedown?: Takedown;
  /** The packages its author uploaded, at most one per target. */
  packages: PackageInfo[];
  /** The web build live at `url`, for a `site` game; null when none is. */
  site: SiteInfo | null;
  /**
   * Whether it is found without its link: an editor featured or verified
   * it, or its author is a member now. A featured or verified game is on
   * every shelf; a member's game is on Pocket TV and in search, and on the
   * shelves of the accounts that put it there. A game host's `/app.json`
   * leaves it out: it reads no membership.
   */
  discoverable?: boolean;
  /**
   * Another author's game the viewer put on their shelf from its link or
   * from Pocket TV, and can take off again. A game every shelf carries (a
   * starter, a featured or verified game) is not kept.
   */
  kept?: boolean;
  /**
   * Whether an editor of Pocket Nexus verified the project. A verified `site`
   * game may deploy a web build to its address; any other `site` game's
   * address shows a page of the Studio's. A starter ships with the Studio and
   * is verified.
   */
  verified: boolean;
  id: string;
  title: string;
  author: string;
  tagline: string;
  origin: AppOrigin;
  /** `draft` apps are visible only to their author. */
  status: "draft" | "published";
  /** Devices the app has a build for. */
  devices: DeviceId[];
  /** Bundle and asset pack URLs per device, absolute paths on this origin. */
  builds: Partial<Record<DeviceId, AppBuild>>;
  /** Screenshot URL for the cover; null until one exists. */
  shot: string | null;
  /** Its listing, once its author uploaded one. */
  listing?: ListingInfo;
  /**
   * The game's page on the Studio's host, such as
   * `https://studio.pocket.nexus/games/tokyo`: its listing, for anyone. Null
   * unless the game is published and has a slug.
   */
  page?: string | null;
  /** Two cover colours, used for the case and menus. */
  colors: [string, string];
  /**
   * Whether a game of one's own can start from a copy of this one's source:
   * a starter, or a published Pocket app whose source the Studio holds (the
   * command line sends it with each build).
   */
  remixable?: boolean;
  /** The game this one was started from as a remix, as it was called then. */
  remixOf?: RemixOf;
  /**
   * For a game built outside Pocket Studio: the public repository of its
   * source, which a remix clones (`pocket-studio remix`). Such a game is
   * `remixable` once published with one.
   */
  repository?: string;
  updatedAt: number;
}

/** A game a remix was started from. */
export interface RemixOf {
  id: string;
  title: string;
  author: string;
}

export interface AppBuild {
  js: string;
  pak: string;
}

export interface RoomInfo {
  id: string;
  title: string;
  /** `premium` rooms come with the membership. */
  tier: "base" | "premium";
  owned: boolean;
  active: boolean;
}

/** One step a creation session passes through. */
export type SessionPhase =
  | "linked"    // a linked command line started the session
  | "scaffold"  // project files written
  | "agent"     // the coding agent is working
  | "build"     // a build is running
  | "preview"   // a build is on the device
  | "published" // the app is on the shelf for everyone
  | "failed";

export interface SessionInfo {
  id: string;
  app: string;
  title: string;
  device: DeviceId;
  agent: AgentId | null;
  phase: SessionPhase;
  /** Last line reported by the CLI. */
  message: string;
  /** 0..1 within the current phase; null when the phase has no measure. */
  progress: number | null;
  /** Where the project is on its maker's computer, as that computer wrote it. Absent until a terminal says. */
  dir?: string;
  /** For a remix: the game its project starts from. */
  remix?: RemixOf;
  /** Increments on every server-side change. */
  rev: number;
  /** Increments each time the CLI uploads a build. */
  build: number;
  updatedAt: number;
}

export interface UserInfo {
  id: string;
  handle: string;
  name: string;
  avatar: string | null;
  guest: boolean;
}

/**
 * The paid plan. Members wear every device colour and mix their own, and
 * download a game's native binary for each device.
 */
export interface MembershipInfo {
  /** The end of the time paid for, in epoch milliseconds; 0 or absent when there is none. */
  paidUntil?: number;
  /** Renewal is cancelled: the membership runs to `paidUntil` and stops. */
  cancelAtPeriodEnd?: boolean;
  /** Price of a year paid at once, in `currency`'s smallest unit. */
  annualPrice?: number;
  /**
   * The currency of `price` and `annualPrice`: "jpy" (yen, no minor unit)
   * for a viewer in Japan, who is charged yen. Absent: US dollars, in cents.
   */
  currency?: "jpy";
  /** A membership an offer gave (src/core/offers.ts), while it is in force: its title, and its end or null for life. */
  grant?: { title: string; until: number | null };
  active: boolean;
  /** Price per period, in `currency`'s smallest unit. */
  price: number;
  period: "month";
}

/** A one-time code that links a coding agent's command line to the account. */
export interface AgentLink {
  code: string;
  /** Epoch milliseconds after which the code no longer links. */
  expiresAt: number;
}


export type Want = "casual";
export const WANTS: readonly Want[] = ["casual"];

/** Everything the room and the laptop need, in one document. */
export interface StudioSnapshot {
  user: UserInfo;
  membership: MembershipInfo;
  devices: DeviceInfo[];
  /** The starters, the user's own games and the published games of others. */
  apps: AppInfo[];
  rooms: RoomInfo[];
  /**
   * Published games of others that Pocket TV shows and that are not on this
   * shelf: members' games, a sample in an order that changes each hour. The
   * room opens one on its card, where the visitor may put it on the shelf.
   * A snapshot from an older server has none.
   */
  aired?: AppInfo[];
  rev: number;
}
